import React, { useEffect, useRef, useState } from 'react'
import { TagPicker } from 'rsuite';
import { capitalizeAllFirstLetters, capitalizeFirstLetter, checkPermission, erpErrorText, formateDateWithMonth, formateDateWithTime, getCompany, showPrice } from '../../helpers/commonFunctions';
import Loader from '../../components/company_panel/common/loader';
import notFound from '../../../src/assets/company/img/no-record-found.png'
import CustomRangepicker from '../../components/company_panel/common/rangepicker';
import { ListZatcaUnsyncedOrders, reportToZatcaAgain } from '../../services/company_panel/orderService';
import Swal from 'sweetalert2';
import { GetBrandOutletsList } from '../../services/company_panel/outletService';
import { GetBrandsHavingOutlets } from '../../services/company_panel/brandService';

// Orders whose ZATCA submission failed: "payment" when the sale invoice failed,
// "refund" when the credit note failed. The API pages by cursor (last_id ->
// next_id), so there is no jumping to page N: the pager steps back and forward,
// remembering the cursor each page was opened with. Retry resends a row's
// invoice or credit note through report-to-zatca-again.

const SYNC_TYPES = {
    payment: { label: 'Payment', title: 'Sale invoice failed', reportType: 'paid' },
    refund: { label: 'Refund', title: 'Credit note failed', reportType: 'refund' },
};

const toast = (icon, title) => Swal.fire({ customClass: 'swal-wide', icon, title, showConfirmButton: false, timer: 3000, toast: true, position: 'top-right' });

const amount = (value) => (value != null ? `${showPrice(value)} SAR` : "-");
const outletName = (row) => row?.outlet_name ? `${capitalizeFirstLetter(row.outlet_name)}${row?.outlet_short_code ? ` (${row.outlet_short_code})` : ''}` : "-";

const ZatcaUnsyncedOrders = () => {
    const [list, setList] = useState([]);
    const [count, setCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loader, setLoader] = useState(true);
    const [itemPerPage, setItemPerPage] = useState(10);
    const [page, setPage] = useState(1);

    const [search, setSearch] = useState({});
    const [orderSearch, setOrderSearch] = useState('');
    const [globalSearch, setGlobalSearch] = useState('');
    const [resetdate, setResetDate] = useState(false);

    const [brandList, setBrandList] = useState([]);
    const [outletList, setOutletList] = useState([]);
    const [selectedBrandIds, setSelectedBrandIds] = useState([]);
    const [selectedOutletIds, setSelectedOutletIds] = useState([]);

    // per row: 'sending' while the resend request is in flight, 'sent' once queued
    const [retryState, setRetryState] = useState({});
    const [refreshFlag, setRefreshFlag] = useState(false);
    // the resend endpoint needs edit on orders; viewing this list only needs view
    const canRetry = checkPermission('store_orders', 'edit');

    // last_id to send for each page; page 1 starts from 0
    const pageCursorsRef = useRef({ 1: 0 });
    const requestRef = useRef(0);

    const resetPaging = () => {
        pageCursorsRef.current = { 1: 0 };
        setPage(1);
    };

    // order id search: typed freely, sent once typing pauses
    useEffect(() => {
        const timer = setTimeout(() => {
            if (orderSearch !== globalSearch) {
                setGlobalSearch(orderSearch);
                resetPaging();
            }
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderSearch]);

    useEffect(() => {
        const requestId = ++requestRef.current;
        setLoader(true);
        setRetryState({});
        ListZatcaUnsyncedOrders({
            per_page: itemPerPage,
            last_id: pageCursorsRef.current[page] || 0,
            global_search: globalSearch,
            search: JSON.stringify(search),
        }).then(data => {
            // a slower, older request must not overwrite a newer one
            if (requestId !== requestRef.current) return;
            const response = data?.data?.response || {};
            setList(response.rows || []);
            setCount(response.count || 0);
            setHasMore(Boolean(response.has_more));
            if (response.has_more && response.next_id != null) {
                pageCursorsRef.current[page + 1] = response.next_id;
            }
            setLoader(false);
        }).catch(error => {
            if (requestId !== requestRef.current) return;
            console.log("error ====> ", error);
            setList([]);
            setCount(0);
            setHasMore(false);
            setLoader(false);
        });
    }, [page, itemPerPage, search, globalSearch, refreshFlag]);

    // Resending only queues the job: ZATCA answers in the background and a
    // successful sync deletes the row server side, so the row stays until the
    // list is refreshed. It is marked as queued meanwhile.
    const handleRetry = (row) => {
        const reportType = SYNC_TYPES[row?.sync_type]?.reportType;
        if (!reportType || row?.order_id == null) return;
        setRetryState(prev => ({ ...prev, [row.id]: 'sending' }));
        const formData = new FormData();
        formData.append('order_id', row.order_id);
        formData.append('report_type', reportType);
        reportToZatcaAgain(formData).then(response => {
            const body = response?.data || {};
            // refusals ("Already reported to zatca", "Payment is pending") come back as 200 too
            if (Number(body.statuscode) === 200 && body.success) {
                setRetryState(prev => ({ ...prev, [row.id]: 'sent' }));
                toast('success', `${body.message || 'Request has been sent to zatca'}. Refresh in a moment to see it clear.`);
            } else {
                setRetryState(prev => { const next = { ...prev }; delete next[row.id]; return next; });
                toast('error', body.message || 'Could not resend to ZATCA');
            }
        }).catch(error => {
            console.log("error ====> ", error);
            setRetryState(prev => { const next = { ...prev }; delete next[row.id]; return next; });
            toast('error', error?.response?.data?.message || 'Could not resend to ZATCA');
        });
    };

    useEffect(() => {
        GetBrandsHavingOutlets(getCompany().company_id).then(data => {
            setBrandList(data?.data?.response || []);
        }).catch(error => {
            console.log("error ====> ", error);
        });
    }, []);

    const prepareSearch = (key, value) => {
        const sr = { ...search };
        if (Array.isArray(value)) {
            if (value.length > 0) { sr[key] = value; } else { delete sr[key]; }
        } else if (value && typeof value === 'object') {
            // the date picker sends {} when cleared
            if (value.start && value.end) { sr[key] = value; } else { delete sr[key]; }
        } else if (String(value ?? '').length > 0) {
            sr[key] = value;
        } else {
            delete sr[key];
        }
        setSearch(sr);
        resetPaging();
    };

    const handleBrandChange = (selectedBrands) => {
        const brands = selectedBrands || [];
        setSelectedBrandIds(brands);
        resetPaging();

        if (brands.length === 0) {
            setOutletList([]);
            setSelectedOutletIds([]);
            const sr = { ...search };
            delete sr.brand_ids;
            delete sr.outlet_ids;
            setSearch(sr);
            return;
        }

        GetBrandOutletsList({ brand_ids: JSON.stringify(brands) }).then(data => {
            const outlets = data?.data?.response || [];
            setOutletList(outlets);
            const validOutletIds = outlets.map(o => o.id);
            const keptOutlets = selectedOutletIds.filter(id => validOutletIds.includes(id));
            setSelectedOutletIds(keptOutlets);

            const sr = { ...search, brand_ids: brands };
            if (keptOutlets.length > 0) { sr.outlet_ids = keptOutlets; } else { delete sr.outlet_ids; }
            setSearch(sr);
        }).catch(error => {
            console.log("error ====> ", error);
        });
    };

    const handleOutletChange = (selectedOutlets) => {
        const outlets = selectedOutlets || [];
        setSelectedOutletIds(outlets);
        prepareSearch('outlet_ids', outlets);
    };

    const removeBrand = (brandId) => handleBrandChange(selectedBrandIds.filter(id => id !== brandId));
    const removeOutlet = (outletId) => handleOutletChange(selectedOutletIds.filter(id => id !== outletId));

    const resetFilter = (e) => {
        e.preventDefault();
        setOrderSearch('');
        setGlobalSearch('');
        setSelectedBrandIds([]);
        setSelectedOutletIds([]);
        setOutletList([]);
        setSearch({});
        setResetDate(!resetdate);
        resetPaging();
    };

    const brandData = brandList.map(item => ({ label: capitalizeAllFirstLetters(item?.name), value: item?.id }));
    const outletData = outletList.map(item => ({
        label: `${capitalizeAllFirstLetters(item?.outlet_name)}${item?.short_code ? ` (${item.short_code})` : ''}`,
        value: item?.id,
    }));

    const pickerValue = (noun) => (value, items) => {
        if (!items.length) return null;
        return (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {items.length === 1 ? items[0]?.label : `${items.length} ${noun} selected`}
            </span>
        );
    };

    const totalPages = Math.max(1, Math.ceil(count / itemPerPage));
    const firstRow = count ? (page - 1) * itemPerPage + 1 : 0;
    const lastRow = Math.min(count, (page - 1) * itemPerPage + list.length);

    return (
        <div className="animation_fade">
            <div className='page-header d-flex align-items-center'>
                <div className='page-header-left'>
                    <h4>Zatca Unsynced Orders</h4>
                </div>
                <div className='page-header-right ms-auto'>
                    <button type="button" className="btn btn-outline-primary" disabled={loader} onClick={() => setRefreshFlag(prev => !prev)}>
                        <i className="ti ti-refresh me-1"></i>Refresh
                    </button>
                </div>
            </div>
            <div className="card custom-card overflow-hidden">
                <div className="gap-20">
                    <div className='page-header d-flex align-items-center mb-0 flex-wrap'>
                        <div className='d-flex gap-3 align-items-center'>
                            <div className="form-group mb-0 rangepicker_container">
                                <input
                                    type="search"
                                    className="form-control"
                                    value={orderSearch}
                                    inputMode="numeric"
                                    placeholder="Search by Order ID"
                                    // the API matches an exact order id, so only digits are useful
                                    onChange={(e) => setOrderSearch(e.target.value.replace(/\D/g, ''))}
                                />
                            </div>
                        </div>
                        <div className='page-header-right ms-auto'>
                            <div className='d-flex gap-3 align-items-center flex-wrap'>
                                <div className="form-group mb-0 filter_icon_container filter_design" style={{ width: '170px' }}>
                                    <select
                                        className="form-control cp"
                                        value={search.sync_type || ''}
                                        onChange={(e) => prepareSearch('sync_type', e.target.value)}
                                    >
                                        <option value="">All Sync Types</option>
                                        {Object.entries(SYNC_TYPES).map(([value, type]) => (
                                            <option key={value} value={value}>{type.label} ({type.title.toLowerCase()})</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group mb-0 multi-filter-picker" style={{ width: '200px' }}>
                                    <TagPicker
                                        data={brandData}
                                        value={selectedBrandIds}
                                        onChange={handleBrandChange}
                                        placeholder="Select Brands"
                                        className='form-control form-control-select'
                                        cleanable={false}
                                        menuStyle={{ maxHeight: 200, overflowY: 'auto' }}
                                        renderValue={pickerValue('brands')}
                                    />
                                </div>
                                <div className="form-group mb-0 multi-filter-picker" style={{ width: '200px' }}>
                                    <TagPicker
                                        data={outletData}
                                        value={selectedOutletIds}
                                        onChange={handleOutletChange}
                                        placeholder="Select Outlets"
                                        className='form-control form-control-select'
                                        cleanable={false}
                                        menuStyle={{ maxHeight: 200, overflowY: 'auto' }}
                                        renderValue={pickerValue('outlets')}
                                    />
                                </div>
                                <div className="date-input">
                                    <CustomRangepicker business_date={true} GetDateRange={(e) => { prepareSearch("business_date", e) }} resetdate={resetdate} />
                                </div>
                                <button type="reset" value='Reset' onClick={resetFilter} className="btn btn-primary mr-2">Reset Filter</button>
                            </div>
                        </div>
                    </div>
                    {selectedBrandIds.length > 0 && (
                        <div className='d-flex flex-wrap gap-2 mt-2'>
                            <span className='text-muted me-1 align-self-center'>Brands:</span>
                            {selectedBrandIds.map(id => {
                                const brand = brandList.find(b => b.id === id);
                                if (!brand) return null;
                                return (
                                    <span key={`b-${id}`} style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#2f9bff', color: '#fff', borderRadius: '14px', padding: '4px 10px', fontSize: '13px', fontWeight: 500 }}>
                                        <span>{capitalizeAllFirstLetters(brand.name)}</span>
                                        <button type='button' aria-label='Remove' onClick={() => removeBrand(id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', marginLeft: 6, padding: 0, lineHeight: 1, color: '#fff', display: 'inline-flex', alignItems: 'center' }}>
                                            <svg width='12' height='12' viewBox='0 0 16 16' fill='currentColor' aria-hidden='true'><path d='M2.784 2.089l.069.058 5.146 5.147 5.146-5.147a.5.5 0 01.765.638l-.058.069L8.705 8l5.147 5.146a.5.5 0 01-.638.765l-.069-.058-5.146-5.147-5.146 5.147a.5.5 0 01-.765-.638l.058-.069L7.293 8 2.146 2.854a.5.5 0 01.638-.765z'></path></svg>
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    {selectedOutletIds.length > 0 && (
                        <div className='d-flex flex-wrap gap-2 mt-2'>
                            <span className='text-muted me-1 align-self-center'>Outlets:</span>
                            {selectedOutletIds.map(id => {
                                const outlet = outletList.find(o => o.id === id);
                                if (!outlet) return null;
                                return (
                                    <span key={`o-${id}`} style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#e7f1ff', color: '#1565c0', border: '1px solid #b6d4fe', borderRadius: '14px', padding: '4px 10px', fontSize: '13px', fontWeight: 500 }}>
                                        <span>{capitalizeAllFirstLetters(outlet.outlet_name)}{outlet.short_code ? ` (${outlet.short_code})` : ''}</span>
                                        <button type='button' aria-label='Remove' onClick={() => removeOutlet(id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', marginLeft: 6, padding: 0, lineHeight: 1, color: '#1565c0', display: 'inline-flex', alignItems: 'center' }}>
                                            <svg width='12' height='12' viewBox='0 0 16 16' fill='currentColor' aria-hidden='true'><path d='M2.784 2.089l.069.058 5.146 5.147 5.146-5.147a.5.5 0 01.765.638l-.058.069L8.705 8l5.147 5.146a.5.5 0 01-.638.765l-.069-.058-5.146-5.147-5.146 5.147a.5.5 0 01-.765-.638l.058-.069L7.293 8 2.146 2.854a.5.5 0 01.638-.765z'></path></svg>
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    {globalSearch ? (
                        <div className="alert alert-info py-2 mt-3 mb-0" style={{ fontSize: '13px' }}>
                            Looking up order #{globalSearch} across the whole company. The other filters are ignored while searching by order ID.
                        </div>
                    ) : null}
                </div>
                <div className="tabler-wrap">
                    <div className="table-responsive">
                        <table className="table table-custom">
                            <thead>
                                <tr>
                                    <th width="12%">Business Date</th>
                                    <th width="9%">Order ID</th>
                                    <th width="12%">Brand</th>
                                    <th width="15%">Outlet</th>
                                    <th width="9%">Sync Type</th>
                                    <th className="text-end" width="11%">Net Amount</th>
                                    <th width="20%">Error</th>
                                    <th width="12%">Failed At</th>
                                    {canRetry ? <th className="text-end" width="9%">Action</th> : null}
                                </tr>
                            </thead>
                            <tbody>
                                {!loader && list.length ? list.map((row, index) => {
                                    const type = SYNC_TYPES[row?.sync_type];
                                    const errorMeta = [row?.error?.function, row?.error?.status].filter(v => v != null && v !== '').join(' · ');
                                    return (
                                        <tr key={row?.id ?? index}>
                                            <td>{row?.business_date ? formateDateWithMonth(row.business_date) : "-"}</td>
                                            <td>{row?.order_id != null ? `#${row.order_id}` : "-"}</td>
                                            <td>{row?.brand_name ? capitalizeAllFirstLetters(row.brand_name) : "-"}</td>
                                            <td>{outletName(row)}</td>
                                            <td>
                                                <span className="status-pill muted" title={type?.title}>{type?.label || capitalizeFirstLetter(row?.sync_type || '-')}</span>
                                            </td>
                                            <td className="text-end">{amount(row?.net_amount)}</td>
                                            <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                <span className="text-danger">{erpErrorText(row?.error) || "-"}</span>
                                                {errorMeta ? <div className="text-muted" style={{ fontSize: '12px' }}>{errorMeta}</div> : null}
                                            </td>
                                            <td>{row?.created_at ? formateDateWithTime(row.created_at) : "-"}</td>
                                            {canRetry ? (
                                                <td className="text-end">
                                                    {retryState[row.id] === 'sent' ? (
                                                        <span className="status-pill success" title="Resend queued; refresh to see whether ZATCA accepted it">Queued</span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-primary btn-sm"
                                                            style={{ height: 'auto', lineHeight: 1.5, whiteSpace: 'nowrap' }}
                                                            disabled={!type || retryState[row.id] === 'sending'}
                                                            title={type ? `Resend the ${type.title.replace(' failed', '').toLowerCase()} to ZATCA` : undefined}
                                                            onClick={() => handleRetry(row)}
                                                        >
                                                            <i className="ti ti-refresh me-1"></i>
                                                            {retryState[row.id] === 'sending' ? 'Sending...' : 'Retry'}
                                                        </button>
                                                    )}
                                                </td>
                                            ) : null}
                                        </tr>
                                    );
                                }) : null}
                            </tbody>
                        </table>
                        {loader ? (
                            <div style={{ position: 'sticky', left: 0 }}><Loader /></div>
                        ) : !list.length ? (
                            <div className="text-center py-3" style={{ position: 'sticky', left: 0 }}>
                                <img src={notFound} style={{ width: '5%' }} alt="Not Found" />
                                <p>No record found</p>
                            </div>
                        ) : null}
                    </div>
                    <div className="tabler-footer">
                        <div className="d-flex align-items-center flex-wrap gap-2">
                            <div role="status" className="table-result" aria-live="polite">
                                <b>Total Records: {count || 0}</b>
                            </div>
                            {count > 0 ? (
                                <>
                                    <div className='ms-3'>
                                        Show
                                        <select
                                            value={itemPerPage}
                                            onChange={(e) => { setItemPerPage(Number(e.target.value)); resetPaging(); }}
                                            className="mx-2"
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                        entries
                                    </div>
                                    <div className="ms-auto d-flex align-items-center gap-3">
                                        <span className="text-muted" style={{ fontSize: '13px' }}>
                                            {firstRow}–{lastRow} of {count} · page {page} of {totalPages}
                                        </span>
                                        <ul className="pagination mb-0">
                                            <li className={`page-item${page === 1 || loader ? ' disabled' : ' cp'}`}>
                                                <button type="button" className="page-link" aria-label="Previous page" disabled={page === 1 || loader} onClick={() => setPage(page - 1)}>‹</button>
                                            </li>
                                            <li className={`page-item${!hasMore || loader ? ' disabled' : ' cp'}`}>
                                                <button type="button" className="page-link" aria-label="Next page" disabled={!hasMore || loader} onClick={() => setPage(page + 1)}>›</button>
                                            </li>
                                        </ul>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ZatcaUnsyncedOrders;
